/**
 * Pure colour science. No DOM, no camera, no app-specific assumptions.
 *
 * This module is the intended reuse boundary: anything that would tie it to a
 * particular capture device, reference card or product belongs one layer up
 * (see `src/calibration/`).
 */

export type { SRGB, LinearRGB, XYZ, Lab, LCh, Illuminant, Matrix3 } from './types.js';
export { WHITE_POINTS, applyMatrix3, IDENTITY_3 } from './types.js';

export {
  srgbTransfer,
  srgbTransferInverse,
  srgbToLinear,
  linearToSrgb,
  srgbFrom8Bit,
  srgbTo8Bit,
  isOutOfGamut,
  srgbToHex,
  hexToSrgb,
} from './srgb.js';

export {
  linearRgbToXyz,
  xyzToLinearRgb,
  adaptXyz,
  LINEAR_SRGB_TO_XYZ_D65,
  XYZ_D65_TO_LINEAR_SRGB,
} from './xyz.js';

export { xyzToLab, labToXyz, labToLch, lchToLab } from './lab.js';

export { deltaE2000, deltaE76, describeDeltaE, type DeltaE2000Weights } from './deltaE.js';

import { srgbToLinear, linearToSrgb } from './srgb.js';
import { linearRgbToXyz, xyzToLinearRgb, adaptXyz } from './xyz.js';
import { xyzToLab, labToXyz } from './lab.js';
import type { Illuminant, Lab, SRGB } from './types.js';

/**
 * Full sRGB -> Lab pipeline.
 *
 * sRGB is defined against D65, so requesting D50 output inserts a Bradford
 * adaptation step.
 */
export function srgbToLab(c: SRGB, illuminant: Illuminant = 'D65'): Lab {
  const xyzD65 = linearRgbToXyz(srgbToLinear(c));
  return xyzToLab(adaptXyz(xyzD65, 'D65', illuminant), illuminant);
}

/** Inverse of {@link srgbToLab}. May return out-of-gamut components. */
export function labToSrgb(c: Lab, illuminant: Illuminant = 'D65'): SRGB {
  const xyz = adaptXyz(labToXyz(c, illuminant), illuminant, 'D65');
  return linearToSrgb(xyzToLinearRgb(xyz));
}
